import React, { useEffect, useState, useCallback } from 'react';
import { useQueryWithErrorHandling } from 'customHooks/errorHandling';
import { useTranslation } from 'react-i18next';
import UserAgendaListComponent from './UserAgendaListComponent';
import { USER_AGENDA_QUERY } from '../queries';
import { SAVE_USER_AGENDA } from '../mutations';
import { USER_AGENDA_UPDATED } from '../subscriptions';
import { get, set, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { useMutation } from '@apollo/client';
import { useToast } from 'utils/hooks';
import DoneAll from '@mui/icons-material/DoneAll';
import { useSubscription } from '@apollo/client';
import { shallowEqual, useSelector } from 'react-redux';
import { isDirty } from '@totalsoft/change-tracking';
import { shape, required, stopOnFirstFailure, email, items } from '@totalsoft/pure-validations';
import { useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { CollapseCard } from '@totalsoft/rocket-ui';

const userAgendaValidator = items(
  shape({
    email: [required, email] |> stopOnFirstFailure
  })
);

const UserAgendaContainer = () => {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [userAgendaLens, dirtyInfo, resetUserAgendaLens] = useChangeTrackingLens([]);
  const userInfo = useSelector(state => state.userInfo, shallowEqual);
  const addToast = useToast();

  const [userAgendaValidation, validateUserAgenda] = useDirtyFieldValidation(userAgendaValidator);

  useQueryWithErrorHandling(USER_AGENDA_QUERY, {
    variables: { userId: userInfo.userId },
    onCompleted: data => {
      resetUserAgendaLens(data.userAgenda);
    },
    fetchPolicy: 'network-only'
  });

  const [saveUserAgenda] = useMutation(SAVE_USER_AGENDA, {
    onError: err => {
      addToast(err.message, 'error');
      setSaving(false);
    }
  });

  useEffect(() => {
    validateUserAgenda(userAgendaLens |> get, dirtyInfo);
  }, [validateUserAgenda, userAgendaLens, dirtyInfo]);

  const handleSaveUserAgenda = useCallback(() => {
    const input = userAgendaLens |> get;
    if (!validateUserAgenda(input)) {
      return;
    }

    setSaving(true);
    saveUserAgenda({
      variables: {
        userId: userInfo.userId,
        input
      }
    });
  }, [saveUserAgenda, userAgendaLens, userInfo.userId, validateUserAgenda]);

  const onAddNewItem = useCallback(() => {
    const newItem = {
      email: null,
      contactName: null,
      userId: userInfo.userId,
      id: null
    };
    const updatedUserAgenda = userAgendaLens |> get;
    const newUserAgenda = [...updatedUserAgenda, newItem];

    (userAgendaLens |> set)(newUserAgenda);
  }, [userAgendaLens, userInfo.userId]);

  const deleteItem = useCallback(
    itemToDelete => {
      const updatedUserAgenda = [...(userAgendaLens |> get)];
      const indexToDelete = updatedUserAgenda.findIndex(item => item === itemToDelete);
      if (indexToDelete !== -1) {
        updatedUserAgenda.splice(indexToDelete, 1);
        (userAgendaLens |> set)(updatedUserAgenda);
      }
    },
    [userAgendaLens]
  );
  useSubscription(USER_AGENDA_UPDATED, {
    onSubscriptionData: ({
      subscriptionData: {
        data: { userAgendaUpdated }
      }
    }) => {
      resetUserAgendaLens(userAgendaUpdated.userAgendas);
      setSaving(false);
      addToast(t('User.UserProfile.Agenda.UserAgendaUpdated'), 'success');
    }
  });

  return (
    <CollapseCard icon={DoneAll} title={t('User.UserProfile.Agenda.Summary.Title')} toggleOnHeaderClick>
      {(userAgendaLens |> get) && (
        <UserAgendaListComponent
          userAgendaLens={userAgendaLens}
          onSave={handleSaveUserAgenda}
          saving={saving}
          isDirtyInfo={isDirty(dirtyInfo)}
          validationInfo={userAgendaValidation}
          onAddNewItem={onAddNewItem}
          deleteItem={deleteItem}
        />
      )}
    </CollapseCard>
  );
};

export default UserAgendaContainer;
